import { LeaseClosedReason } from "@akashnetwork/chain-sdk/private-types/akash.v1";

import type { LeaseDto } from "@src/types/deployment";

export type LeaseCloseParty = "tenant" | "provider" | "network" | "unknown";

type ReclaimableLease = Pick<LeaseDto, "state" | "reason" | "reclamation" | "group">;

/**
 * The chain encodes *who* closed a lease in the numeric range of `LeaseClosedReason`:
 *   1..9999     owner / tenant
 *   10000..19999 provider
 *   20000..29999 network (insufficient funds)
 * REST serializes the reason as the enum *name* (e.g. "lease_closed_reason_unstable"), but we also
 * accept a numeric string for resilience. `LeaseClosedReason` is a reverse-mappable enum, so a name
 * resolves to its number without needing the (unexported) `leaseClosedReasonFromJSON`.
 */
function toNumericCloseReason(reason?: string): number | undefined {
  if (!reason) return undefined;

  const asNumber = Number(reason);
  if (reason.trim() !== "" && !Number.isNaN(asNumber)) return asNumber;

  const mapped = (LeaseClosedReason as Record<string, number | string>)[reason];
  return typeof mapped === "number" ? mapped : undefined;
}

export function classifyLeaseCloseReason(reason?: string): LeaseCloseParty {
  const value = toNumericCloseReason(reason);
  if (value === undefined) return "unknown";
  if (value >= 1 && value <= 9999) return "tenant";
  if (value >= 10000 && value <= 19999) return "provider";
  if (value >= 20000 && value <= 29999) return "network";
  return "unknown";
}

export function getLeaseCloseReasonLabel(reason?: string): string {
  switch (toNumericCloseReason(reason)) {
    case LeaseClosedReason.lease_closed_owner:
      return "Closed by you";
    case LeaseClosedReason.lease_closed_reason_unstable:
      return "Closed by provider (workloads unstable)";
    case LeaseClosedReason.lease_closed_reason_decommission:
      return "Closed by provider (decommissioned)";
    case LeaseClosedReason.lease_closed_reason_manifest_timeout:
      return "Closed by provider (manifest timeout)";
    case LeaseClosedReason.lease_closed_reason_insufficient_funds:
      return "Closed (insufficient funds)";
    default:
      // Covers the provider "unspecified" reason plus tenant/network buckets without a specific copy.
      return classifyLeaseCloseReason(reason) === "provider" ? "Closed by provider" : "Closed";
  }
}

export function isClosedByOwner(lease: ReclaimableLease): boolean {
  return toNumericCloseReason(lease.reason ?? lease.reclamation?.reason) === LeaseClosedReason.lease_closed_owner;
}

/**
 * The same close reason at list density, where a parenthetical detail would wrap the status badge onto a
 * second line; the full label stays on the badge's tooltip and on the deployment detail page.
 */
export function getClosedLeaseSummaryLabel(lease: ReclaimableLease): string {
  switch (toNumericCloseReason(lease.reason ?? lease.reclamation?.reason)) {
    case LeaseClosedReason.lease_closed_reason_unstable:
    case LeaseClosedReason.lease_closed_reason_decommission:
    case LeaseClosedReason.lease_closed_reason_manifest_timeout:
      return "Closed by provider";
    case LeaseClosedReason.lease_closed_reason_insufficient_funds:
      return "Out of funds";
    default:
      return getClosedLeaseLabel(lease);
  }
}

/**
 * How a closed lease reads to its owner: who closed it and, where the chain says so, why. A reclaimed lease
 * whose reason didn't classify (e.g. only the group is paused) still reads as provider-closed.
 */
export function getClosedLeaseLabel(lease: ReclaimableLease): string {
  const label = getLeaseCloseReasonLabel(lease.reason ?? lease.reclamation?.reason);
  return label === "Closed" && isProviderReclaimed(lease) ? "Closed by provider" : label;
}

/**
 * Parse a bid's offered reclamation window (REST Duration, e.g. "86400s") to seconds, or null when
 * absent/empty/non-positive/unparseable. The trailing "s" is optional for resilience against a bare
 * seconds count. Used to format the offered window in the bid-selection UI (AEP-82).
 */
export function parseReclamationWindowSeconds(reclamationWindow?: string): number | null {
  if (!reclamationWindow) return null;
  const seconds = Number(reclamationWindow.trim().replace(/s$/, ""));
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return seconds;
}

/** Reclamation deadline as a Date, or null when absent/0/NaN. `deadline` is unix seconds. */
export function getReclamationDeadline(lease: Pick<LeaseDto, "reclamation">): Date | null {
  const deadline = lease.reclamation?.deadline;
  // `!deadline` rejects undefined/0/NaN; `!Number.isFinite` additionally rejects ±Infinity, any of
  // which would otherwise produce an Invalid Date and break the downstream countdown formatting.
  if (!deadline || !Number.isFinite(deadline)) return null;
  return new Date(deadline * 1000);
}

export function isReclaiming(lease: Pick<LeaseDto, "state">): boolean {
  return lease.state === "reclaiming";
}

/** The soonest deadline among a deployment's reclaiming leases: the moment the workload actually goes dark. */
export function getNearestReclamationDeadline(leases: LeaseDto[] | null | undefined): Date | null {
  const deadlines = (leases ?? [])
    .filter(isReclaiming)
    .map(getReclamationDeadline)
    .filter((deadline): deadline is Date => deadline !== null);
  if (deadlines.length === 0) return null;
  return deadlines.reduce((earliest, current) => (current < earliest ? current : earliest));
}

function hasReclamationStarted(lease: Pick<LeaseDto, "reclamation">): boolean {
  const startedAt = lease.reclamation?.startedAt;
  return startedAt !== undefined && Number(startedAt) > 0;
}

/**
 * Terminal reclamation: the provider has reclaimed the lease and it is no longer live.
 * "Reclaimed" requires reclamation *evidence* (reclamation started, or the group is paused) — a bare
 * provider-range close reason alone is NOT enough, since a provider can close a lease directly
 * without going through the AEP-82 reclamation grace period.
 */
export function isProviderReclaimed(lease: ReclaimableLease): boolean {
  if (isReclaiming(lease)) return false;
  return hasReclamationStarted(lease) || lease.group?.state === "paused";
}
